import { useState } from 'react'
import { useNewCode } from '@calendly/feature-check-react'

import { useIsAnalyticsAllowed } from 'hooks/useIsAnalyticsAllowed'

import { useConfirmationPageData } from './useConfirmationPageData'
import { CookieCounter, SharedObjectStorage, getCookieCount } from './shared-storage'

/*
  # Booking Cookie Storage

  ## Overview
  Stores booking data in cookies for cross-application sharing on calendly.com domain.

  ## Data Stored
  - **PII**: First/last name, email, publisher name (1 day expiration)
  - **Analytics**: Booking counters (30 day expiration)

  ## Privacy & Consent
  - Respects OneTrust cookie consent preferences via useIsAnalyticsAllowed hook
  - Uses 'targeting' category for marketing/conversion cookies
  - Excludes preview mode to prevent test data pollution
  - Only sets cookies if user has opted into analytics/targeting cookies
  - Gracefully degrades when consent is not given

  ## Security
  - Input sanitization prevents XSS attacks
  - `Secure` and `SameSite=Lax` flags
  - 3KB size limit with auto-truncation
  - Graceful degradation when cookies disabled
 */


/**
 * @description This component is used to persist booking information as cookies
 * @returns {JSX.Element} The JSX element
 */
export const BookingStorage = () => {
  const { eventType, inviteeEmail, firstName, lastName, inviteeUuid } = useConfirmationPageData()
  const counterKey = 'invitee_uuid'
  const [count, setCount] = useState(() => getCookieCount(counterKey))

  const bookingStorageEnabled = useNewCode('growth_booking_data_storage')?.enabled
  const isAnalyticsAllowed = useIsAnalyticsAllowed()
  if (!bookingStorageEnabled || !isAnalyticsAllowed) return null

  return (
    <>
      <CookieCounter
        keyName={counterKey}
        id={inviteeUuid}
        onUpdate={setCount}
      />
      <SharedObjectStorage value={{
        invitee_first_name: firstName,
        invitee_last_name: lastName,
        invitee_email: inviteeEmail,
        invitee_booked_publisher_name: eventType?.profile?.name,
        invitee_booked_count: count,
      }}
      />
    </>
  )
}
